/**
 * 배관 압력손실 — 일반 유체역학.
 *
 * ⚠ **이 모듈의 식은 API 표준의 식이 아니다.**
 * API 520 Part II 는 "무엇을 손실에 포함할 것인가"(7.3.7.2)만 규정하고,
 * 압력강하 계산 자체는 일반 유체역학에 맡긴다. 그래서 여기서는
 * Darcy–Weisbach 식과 Colebrook–White 마찰계수를 쓴다.
 * 계산서에도 그렇게 표기해야 한다 — API 식인 것처럼 적으면 안 된다.
 *
 * 단위: 유량 kg/s · 밀도 kg/m³ · 관경 m · 길이 m · 압력 Pa · 점도 Pa·s
 */

/** 층류/난류 경계. 이 아래는 Hagen–Poiseuille (f = 64/Re). */
export const LAMINAR_LIMIT_RE = 2000;
/** 완전 난류로 보는 하한. 이 사이는 천이영역이라 값이 불확실하다. */
export const TURBULENT_LOWER_RE = 4000;

/** 흔히 쓰는 배관 절대조도 (m). 사내 기준이 있으면 그것을 쓴다. */
/**
 * 재질별 절대조도 (m).
 *
 * 출처: **KOSHA GUIDE D-63-2018 <표2> 재질별 절대표면거칠기**
 * (API 표준에는 이 표가 없다.)
 *
 * ⚠ API 520 Part II 7.3.7.2 a) 는 "시간이 지나며 내면이 나빠질 것을
 *   고려하라"고 한다. 신관 값만 쓰지 말 것.
 */
export const PIPE_ROUGHNESS_M = {
  /** 탄소강 신관 — KOSHA 표2 "Carbon Steel (New)" 0.02~0.05, 0.045 널리 사용 */
  commercialSteel: 0.000045,
  /** 인발관·유리·플라스틱 — KOSHA 표2 "Drawn Tubing, Glass, Plastic" 0.0015~0.01 */
  drawnTubing: 0.0000015,
  /** 스테인리스강 — KOSHA 표2 "Stainless Steel" 0.03 */
  stainlessSteel: 0.00003,
  /** 탄소강 경미 부식 — KOSHA 표2 "Carbon Steel (Slightly Corroded)" 0.05~0.15 */
  slightlyCorroded: 0.0001,
  /** 탄소강 중간 부식 — KOSHA 표2 "Carbon Steel (Moderately Corroded)" 0.15~1.0 */
  corrodedSteel: 0.0005,
  /** 탄소강 심한 부식 — KOSHA 표2 "Carbon Steel (Badly Corroded)" 1.0~3.0 */
  badlyCorroded: 0.002,
} as const;

/**
 * 참고용 부속 손실계수 K.
 *
 * ⚠ API 표준값이 아니라 일반적으로 통용되는 값이다. 사내 기준이나
 * Crane TP-410 같은 자료가 있으면 그쪽을 쓰고 여기 값은 참고로만 둔다.
 * 입구 손실(용기 → 입구배관)은 7.3.7.2 a) 가 **반드시 포함하라**고 한다.
 */
export const FITTING_K = {
  /** 용기에서 배관으로 들어가는 각진 입구 */
  sharpEntrance: 0.5,
  /** 안쪽으로 튀어나온 입구 */
  reentrantEntrance: 0.78,
  /** 둥근 입구 */
  roundedEntrance: 0.05,
  elbow90LongRadius: 0.3,
  elbow90ShortRadius: 0.75,
  elbow45: 0.2,
  teeThrough: 0.2,
  teeBranch: 1.0,
  gateValveFullOpen: 0.15,
  ballValveFullBore: 0.05,
} as const;

/** 배관 내경으로 단면적 (m²) */
export function pipeArea(diameterM: number): number {
  return (Math.PI * diameterM * diameterM) / 4;
}

/** 질량유량에서 유속 (m/s) */
export function velocity(
  massFlowKgS: number,
  densityKgM3: number,
  diameterM: number,
): number {
  return massFlowKgS / (densityKgM3 * pipeArea(diameterM));
}

/** 레이놀즈수. μ 는 동점도가 아니라 절대점도(Pa·s). */
export function reynolds(
  massFlowKgS: number,
  diameterM: number,
  viscosityPaS: number,
): number {
  // Re = ρvD/μ = 4·ṁ/(π·D·μ)  — 밀도가 약분된다
  return (4 * massFlowKgS) / (Math.PI * diameterM * viscosityPaS);
}

/**
 * Colebrook–White 마찰계수의 명시적 초기값 (Swamee–Jain).
 *   f0 = 0.25 / [ log10( ε/(3.7D) + 5.74/Re^0.9 ) ]²
 */
export function swameeJainFriction(re: number, relativeRoughness: number): number {
  const t = Math.log10(relativeRoughness / 3.7 + 5.74 / Math.pow(re, 0.9));
  return 0.25 / (t * t);
}

/**
 * Darcy 마찰계수.
 *
 * 층류(Re < 2000)는 f = 64/Re.
 * 난류는 Colebrook–White 를 반복해서 푼다:
 *   1/√f = −2·log10( ε/(3.7D) + 2.51/(Re·√f) )
 * Swamee–Jain 을 초기값으로 쓰면 대개 3~4회에 수렴한다.
 */
export function frictionFactor(re: number, relativeRoughness: number): number {
  if (!Number.isFinite(re) || re <= 0) return Number.NaN;
  if (re < LAMINAR_LIMIT_RE) return 64 / re;

  let f = swameeJainFriction(re, relativeRoughness);
  for (let i = 0; i < 60; i += 1) {
    const inv = -2 * Math.log10(relativeRoughness / 3.7 + 2.51 / (re * Math.sqrt(f)));
    const next = 1 / (inv * inv);
    if (Math.abs(next - f) < 1e-12) return next;
    f = next;
  }
  return f;
}

export interface PipeSegment {
  /**
   * 계산에 쓰는 길이 (m).
   * 등가길이 방식이면 **직관 길이 + 부속 등가길이**를 넣는다.
   */
  readonly lengthM: number;
  /** 내경 (m) */
  readonly innerDiameterM: number;
  /** 절대조도 (m) */
  readonly roughnessM: number;
  /**
   * 손실계수 합 ΣK.
   * 등가길이 방식에서는 길이로 환산되지 않는 부속(입구·출구)만 남는다.
   * 용기 입구 손실을 반드시 포함할 것 (API 520 Part II 7.3.7.2 a).
   */
  readonly fittingK: number;
}

/** 압력손실 계산 방식. 계산서에 어느 쪽을 썼는지 남겨야 한다. */
export type LossMethod = "equivalent-length" | "resistance-coefficient";

/**
 * 부속을 반영한 배관 구간을 만든다.
 *
 * - `equivalent-length`: 부속을 등가길이로 바꿔 직관 길이에 더한다.
 *   길이 개념이 없는 입구·출구는 K 로 남긴다.
 * - `resistance-coefficient`: 부속을 전부 K 로 다룬다(Crane 방식).
 *   길이는 직관만 쓴다.
 *
 * @param straightLengthM 직관 길이 (m)
 */
export function buildSegment(
  straightLengthM: number,
  innerDiameterM: number,
  roughnessM: number,
  fittings: { equivalentLengthM: number; headOnlyK: number; totalKEquivalent: number },
  method: LossMethod,
): PipeSegment {
  return method === "equivalent-length"
    ? {
        lengthM: straightLengthM + fittings.equivalentLengthM,
        innerDiameterM,
        roughnessM,
        fittingK: fittings.headOnlyK,
      }
    : {
        lengthM: straightLengthM,
        innerDiameterM,
        roughnessM,
        fittingK: fittings.totalKEquivalent,
      };
}

export interface PressureDropResult {
  /** 비회복 압력손실 (Pa) */
  readonly pressureDropPa: number;
  readonly velocityMs: number;
  readonly reynolds: number;
  readonly frictionFactor: number;
  /** 마찰(직관) 몫 (Pa) */
  readonly frictionPa: number;
  /** 부속 몫 (Pa) */
  readonly fittingPa: number;
  /** 속도수두 ρv²/2 (Pa) */
  readonly velocityHeadPa: number;
  readonly flowRegime: "laminar" | "transitional" | "turbulent";
}

/**
 * Darcy–Weisbach 비회복 압력손실.
 *
 *   ΔP = ( f·L/D + ΣK ) × ρ·v² / 2
 *
 * 운동에너지 손실과 액주 정압은 **회복 가능**하므로 포함하지 않는다
 * (API 520 Part II 7.3.7.2 c, d).
 */
export function pressureDrop(
  segment: PipeSegment,
  massFlowKgS: number,
  densityKgM3: number,
  viscosityPaS: number,
): PressureDropResult {
  const v = velocity(massFlowKgS, densityKgM3, segment.innerDiameterM);
  const re = reynolds(massFlowKgS, segment.innerDiameterM, viscosityPaS);
  const relRough = segment.roughnessM / segment.innerDiameterM;
  const f = frictionFactor(re, relRough);

  const velocityHead = (densityKgM3 * v * v) / 2;
  const frictionPa = (f * segment.lengthM) / segment.innerDiameterM * velocityHead;
  const fittingPa = segment.fittingK * velocityHead;

  return {
    pressureDropPa: frictionPa + fittingPa,
    velocityMs: v,
    reynolds: re,
    frictionFactor: f,
    frictionPa,
    fittingPa,
    velocityHeadPa: velocityHead,
    flowRegime:
      re < LAMINAR_LIMIT_RE
        ? "laminar"
        : re < TURBULENT_LOWER_RE
          ? "transitional"
          : "turbulent",
  };
}
